import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

import { TeamsLeaderboardsQuery } from '../../../types/__generated__';

import PageSection from '../PageSection/PageSection';
import BenefactorListItemSkeleton from '../BenefactorListItem/BenefactorListItemSkeleton';
import useFullPageQuery from '../../hooks/useFullPageQuery';

import { TeamsList } from './TeamsList';

import TeamsLeaderboard from './Leaderboards.gql';
import { Team } from './Team';
import { NetworkStatus } from '@apollo/client';

export const TeamsLeaderboardList: FunctionComponent = () => {
  const { t } = useTranslation('common', { useSuspense: false });
  const { loading, error, data, fetchMore, networkStatus } = useFullPageQuery<
    TeamsLeaderboardsQuery
  >(TeamsLeaderboard, {
    notifyOnNetworkStatusChange: true,
  });

  if (
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.refetch ||
    error
  ) {
    return (
      <PageSection title={t('page.teamsListTitle', 'Teams')} hasBorder>
        <BenefactorListItemSkeleton animated={false} />
      </PageSection>
    );
  }

  const leaderboard = data?.page?.leaderboard;

  if (!data || !leaderboard || leaderboard.totalCount === 0) {
    return null;
  }

  async function handleFetchMore() {
    await fetchMore({
      variables: {
        after: leaderboard?.pageInfo.endCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (
          !previousResult?.page?.leaderboard ||
          !fetchMoreResult?.page?.leaderboard
        ) {
          return previousResult;
        }

        return {
          page: {
            ...previousResult.page,
            leaderboard: {
              ...fetchMoreResult.page.leaderboard,
              edges: [
                ...previousResult.page.leaderboard.edges,
                ...fetchMoreResult.page.leaderboard.edges,
              ],
            },
          },
        };
      },
    });
  }

  const teamPages: Team[] = leaderboard?.edges?.map(edge => ({
    id: edge.node.id,
    donationsTotalCount: edge.node.donations?.totalCount,
    avatar: edge.node.owner?.avatar,
    target: edge.node.targetWithCurrency?.value,
    currencyCode: edge.node.donationSummary?.totalAmount?.currencyCode,
    donationTotalAmount: edge.node.donationSummary?.totalAmount?.value,
    slug: edge.node.slug,
    title: edge.node.title,
    status: edge.node.status,
  }));

  return (
    <TeamsList
      loading={loading}
      hasNextPage={leaderboard.pageInfo.hasNextPage}
      handleFetchMore={handleFetchMore}
      teamPages={teamPages}
      totalCount={leaderboard.totalCount}
      isLeaderboardTeamsList={true}
    />
  );
};
